import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
    AfterViewInit,
    Component,
    ContentChild,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { OptionsInput } from '@cargos/sprintpay-models';
import { PhoneInputComponent } from '@cargos/sprintpay-ui';
import { CustomMasks } from '@cargos/sprintpay-utils';
import { MtxColorpickerModule } from '@ng-matero/extensions/colorpicker';
import { Subject, combineLatest, startWith, takeUntil } from 'rxjs';
import {
    DynamicFieldBaseName,
    DynamicFieldForm,
    DynamicFieldValidation,
} from '../../shared/components/dynamic-field-form/models/dynamic-field-form';
import { SectionHeaderComponent } from '../../shared/components/section-header/section-header.component';
import { BrandingColors } from '../../shared/models/branding.model';
import { DomainHostingControls, DomainType } from '../../shared/models/domain.model';
import { OrganizationData } from '../../shared/models/organization-data.model';
import { PaidToFacilityFormControls } from '../../shared/models/paid-to-facility.model';
import { InputPatterns } from '../../shared/utils/input-patterns';
import { incrementVersion } from '../../shared/utils/version.utils';
import { BrandingSectionComponent } from './components/branding-section-component/branding-section-component.component';
import { DomainHostingSectionComponent } from './components/domain-hosting-section/domain-hosting-section.component';
import { FeatureSectionComponent } from './components/feature-section-component/feature-section.component';
import { LegalAndTermsSectionComponent } from './components/legal-and-terms-section-component/legal-and-terms-section.component';
import {
    WorkingRangeControls,
    WorkingScheduleControls,
} from './components/organization-details-section-component/models/working-schedule';
import { OrganizationDetailsSectionComponent } from './components/organization-details-section-component/organization-details-section.component';
import { PaymentSettingsComponent } from './components/payment-settings-component/payment-settings-component.component';
import { FORM_SECTION_DESCRIPTIONS } from './const/form-help-text.const';
import {
    AppContactPhoneForm,
    AppFeaturesForm,
    AppFeaturesShipmentOrSearchForm,
    AppForm,
    AppFormMode,
    DynamicFieldPersistent,
    FaqURLType,
    FooterSectionFormControls,
    HeaderConfigFormControls,
} from './models/app-form';
import { FormAppService } from './services/form-app.service';
import { overlappingRangesValidator } from './validators/validators';
@Component({
    selector: 'app-form-app',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatToolbarModule,
        MatSidenavModule,
        MatIconModule,
        MatButtonModule,
        MatInputModule,
        MatDividerModule,
        MatSlideToggleModule,
        MatExpansionModule,
        MatProgressSpinnerModule,
        MatCardModule,
        MatSelectModule,
        MatCardModule,
        MatRadioModule,
        MtxColorpickerModule,
        OrganizationDetailsSectionComponent,
        BrandingSectionComponent,
        PaymentSettingsComponent,
        LegalAndTermsSectionComponent,
        FeatureSectionComponent,
        DomainHostingSectionComponent,
        SectionHeaderComponent,
    ],
    templateUrl: './form-app.component.html',
    styleUrl: './form-app.component.scss',
})
export class FormAppComponent implements AfterViewInit, OnDestroy, OnInit {
    public loading: boolean = false;
    public form: FormGroup<AppForm>;
    public readonly contactInformationDescription = FORM_SECTION_DESCRIPTIONS.contactInformation;
    public availableFeatures: string[] = [
        'Authentication',
        'Analytics',
        'Push Notifications',
        'Chat Support',
        'Payment Integration',
    ];
    public lightLogoUrl: string = '';
    public darkLogoUrl: string = '';
    public isUploadingBackgoundImg = false;
    private unsubscribe$: Subject<void> = new Subject<void>();
    public _mode: AppFormMode;
    public persistentFields: DynamicFieldPersistent[] = [];
    public extraFieldNameRegex: RegExp = new RegExp(`^${DynamicFieldBaseName}\\d+$`);
    public firebaseOrganizationData: OrganizationData | undefined;

    @ViewChild(PhoneInputComponent) phoneInputComponent: PhoneInputComponent;
    @ContentChild(TemplateRef) buttonSubmitTemplateRef: TemplateRef<unknown>;
    @Input() titleForm: string;
    @Input()
    set mode(mode: AppFormMode) {
        this._mode = mode;

        if (mode === AppFormMode.EDITION && this.form) {
            this.initEdition();
        }
    }
    @Output() onSubmit = new EventEmitter<OrganizationData>();
    @ViewChild('orgDetails') orgDetails?: OrganizationDetailsSectionComponent;
    @ViewChild('brandingSection') brandingSection?: BrandingSectionComponent;
    @ViewChild('paymentSettings') paymentSettings?: PaymentSettingsComponent;
    @ViewChild('legalSection') legalAndTermsSectionComponent?: LegalAndTermsSectionComponent;
    @ViewChild('featureSection') featureSectionComponent?: FeatureSectionComponent;
    @ViewChild('domainHostingSection') domainHostingSection?: DomainHostingSectionComponent; // ← NEW

    get facilityIdControl(): FormControl<string | null> {
        return this.form?.controls?.paidToFacilityId;
    }

    get mainLogoLightUrlControl(): FormControl<string | null> {
        return this.form?.controls?.mainLogoLightUrl;
    }

    get mainLogoDarkUrlControl(): FormControl<string | null> {
        return this.form?.controls?.mainLogoDarkUrl;
    }

    get favIconURLControl(): FormControl<string | null> {
        return this.form?.controls?.favIconURL;
    }

    get primaryContactName(): FormControl<string | null> {
        return this.form?.controls?.primaryContactName;
    }

    get contactEmail(): FormControl<string | null> {
        return this.form?.controls?.contactEmail;
    }

    get phone(): FormControl<string | null> {
        return this.form?.controls?.phone;
    }

    get dynamicFields(): FormArray<FormGroup<DynamicFieldForm>> {
        return this.form?.controls?.features?.controls?.shipmentForm?.controls?.dynamicFields;
    }

    get searchDynamicFields(): FormArray<FormGroup<DynamicFieldForm>> {
        return this.form?.controls?.features?.controls?.searchForm?.controls?.dynamicFields;
    }

    get isEdition(): boolean {
        return this._mode?.valueOf() === AppFormMode.EDITION;
    }

    get alternativeLogoWidthControl(): FormControl<string | null> {
        return this.form?.controls?.alternativeLogoWidth;
    }

    get alternativeLogoHeightControl(): FormControl<string | null> {
        return this.form?.controls?.alternativeLogoHeight;
    }

    get mainLogoWidthControl(): FormControl<string | null> {
        return this.form?.controls?.mainLogoWidth;
    }

    get mainLogoHeightControl(): FormControl<string | null> {
        return this.form?.controls?.mainLogoHeight;
    }

    get headerConfig(): FormGroup<HeaderConfigFormControls> {
        return this.featuresControls.controls.headerConfig;
    }

    get featuresControls(): FormGroup<AppFeaturesForm> {
        return this.form.controls.features;
    }

    constructor(
        private snackBar: MatSnackBar,
        private formAppService: FormAppService,
        private http: HttpClient
    ) {}

    ngOnInit(): void {
        this.createForm();
        this.subscribeSlugAutofill();
        if (this._mode === AppFormMode.EDITION) {
            this.initEdition();
        }
    }

    ngAfterViewInit(): void {
        this.subscribeOrganization();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private subscribeSlugAutofill(): void {
        const domainHostingControls = this.form.controls.domainHosting.controls;
        const organizationName$ = this.form.controls.organizationName.valueChanges.pipe(
            startWith(this.form.controls.organizationName.value)
        );
        const domainType$ = domainHostingControls.domainType.valueChanges.pipe(
            startWith(domainHostingControls.domainType.value)
        );

        combineLatest([organizationName$, domainType$])
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(([name, domainType]) => {
                if (domainType === 'subdomain' && name) {
                    domainHostingControls.domainValue.setValue(this.toSlug(name), { emitEvent: false });
                }
            });
    }

    private toSlug(value: string): string {
        return value
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-{2,}/g, '-')
            .replace(/^-|-$/g, '');
    }

    createForm(): void {
        this.form = new FormGroup<AppForm>({
            primaryContactName: new FormControl('', [Validators.required]),
            contactEmail: new FormControl('', [Validators.required, Validators.email]),
            phone: new FormControl('', [Validators.required]),
            organizationName: new FormControl('', [Validators.required]),
            paidToFacilityId: new FormControl(null, [
                Validators.required,
                Validators.pattern(CustomMasks.alphaNumeric.mask),
            ]),
            paidToFacilitiesId: new FormArray<FormGroup<PaidToFacilityFormControls>>([]),
            paidToMultipleFacilitiesEnable: new FormControl(false),
            allowMultiplePayments: new FormControl(false),
            customerReferenceRequired: new FormControl(true),
            email: new FormControl('', [Validators.required, Validators.email]),
            contactPhone: new FormGroup<AppContactPhoneForm>({
                code: new FormControl('', [Validators.required]),
                phone: new FormControl('', [Validators.required]),
            }),
            alternativeLogoLightUrl: new FormControl(
                'https://firebasestorage.googleapis.com/v0/b/cargo-facilities-studio.firebasestorage.app/o/logos%2FCargoSprint-Horizontal.svg?alt=media&token=e15fa9e8-fe75-43a0-8eff-161d8f01015a',
                [Validators.required, Validators.pattern(InputPatterns.URL)]
            ),
            alternativeLogoDarkUrl: new FormControl(
                'https://firebasestorage.googleapis.com/v0/b/cargo-facilities-studio.firebasestorage.app/o/logos%2FCargoSprint-Horizontal.svg?alt=media&token=e15fa9e8-fe75-43a0-8eff-161d8f01015a',
                [Validators.required, Validators.pattern(InputPatterns.URL)]
            ),
            alternativeLogoWidth: new FormControl('auto', [Validators.required]),
            alternativeLogoHeight: new FormControl('auto', [Validators.required]),
            mainLogoLightUrl: new FormControl(
                'https://firebasestorage.googleapis.com/v0/b/cargo-facilities-studio.firebasestorage.app/o/logos%2FCargoSprint-Horizontal.svg?alt=media&token=e15fa9e8-fe75-43a0-8eff-161d8f01015a',
                [Validators.required, Validators.pattern(InputPatterns.URL)]
            ),
            mainLogoDarkUrl: new FormControl(
                'https://firebasestorage.googleapis.com/v0/b/cargo-facilities-studio.firebasestorage.app/o/logos%2FCargoSprint-Horizontal.svg?alt=media&token=e15fa9e8-fe75-43a0-8eff-161d8f01015a',
                [Validators.required, Validators.pattern(InputPatterns.URL)]
            ),
            mainLogoWidth: new FormControl('auto', [Validators.required]),
            mainLogoHeight: new FormControl('auto', [Validators.required]),
            setMainLogoToHeader: new FormControl(true),
            setMainLogoToLogin: new FormControl(true),
            setAlternativeLogoToHeader: new FormControl(false),
            setAlternativeLogoToLogin: new FormControl(false),
            brandingColors: new FormControl<BrandingColors>(
                {
                    primary: {
                        color: '#016C78',
                        tones: { lightTone: 50, primaryTone: 30, darkTone: 20 },
                    },
                    secondary: {
                        color: '#0C2245',
                        tones: { lightTone: 60, primaryTone: 30, darkTone: 20 },
                    },
                    success: {
                        color: '#4caf50',
                        tones: { lightTone: 80, primaryTone: 50, darkTone: 40 },
                    },
                    error: {
                        color: '#f44336',
                        tones: { lightTone: 70, primaryTone: 50, darkTone: 40 },
                    },
                    warn: {
                        color: '#fcb900',
                        tones: { lightTone: 60, primaryTone: 40, darkTone: 20 },
                    },
                    info: {
                        color: '#143981',
                        tones: { lightTone: 80, primaryTone: 50, darkTone: 40 },
                    },
                },
                { nonNullable: true }
            ),
            termsUrl: new FormControl('https://cargosprint.com/legal/terms-of-use/#sprintpay/', [
                Validators.required,
                Validators.pattern(InputPatterns.URL),
            ]),
            privacyUrl: new FormControl('https://cargosprint.com/legal/privacy-policy', [
                Validators.required,
                Validators.pattern(InputPatterns.URL),
            ]),
            faqURL: new FormControl('', [Validators.pattern(InputPatterns.URL)]),
            favIconURL: new FormControl('', [Validators.pattern(InputPatterns.URL)]),
            workingScheduleForm: new FormGroup<WorkingScheduleControls>({
                timeZone: new FormControl(''),
                ranges: new FormArray<FormGroup<WorkingRangeControls>>([], overlappingRangesValidator()),
            }),
            domainHosting: new FormGroup<DomainHostingControls>({
                domainType: new FormControl<DomainType>('subdomain', Validators.required),
                domainValue: new FormControl<string>('', Validators.required),
            }),
            features: new FormGroup<AppFeaturesForm>({
                headerConfig: new FormGroup<HeaderConfigFormControls>({
                    headerBackgroundColor: new FormControl(''),
                    headerTextColor: new FormControl(''),
                    isGradientEnabled: new FormControl(false),
                    headerBackgroundColor100: new FormControl(''),
                    headerBackgroundColor30: new FormControl(''),
                    headerBackgroundColor62: new FormControl(''),
                    headerTextActiveColor: new FormControl(false),
                }),
                allowShipmentForm: new FormControl<boolean>(true),
                allowSearchForm: new FormControl<boolean>(false),
                enableDarkMode: new FormControl(false),
                removeShadow: new FormControl(false),
                shipmentForm: new FormGroup<AppFeaturesShipmentOrSearchForm>({
                    dynamicFields: new FormArray<FormGroup<DynamicFieldForm>>([]),
                    backgroundType: new FormControl('halfBackground'),
                    backgroundImages: new FormArray<FormGroup<{ name: FormControl<string | null> }>>([]),
                    backgroundColor: new FormControl<string>(''),
                }),
                typeFlow: new FormControl<'shipmentForm' | 'searchForm'>('shipmentForm'),
                searchForm: new FormGroup<AppFeaturesShipmentOrSearchForm>({
                    dynamicFields: new FormArray<FormGroup<DynamicFieldForm>>([]),
                    enableSearchByFlightNumber: new FormControl<boolean>(false),
                    backgroundType: new FormControl('halfBackground'),
                    backgroundImages: new FormArray<FormGroup<{ name: FormControl<string | null> }>>([]),
                    backgroundColor: new FormControl<string>(''),
                    facilitySource: new FormControl(''),
                }),
                showBreadcrumbs: new FormControl(false),
                showFooter: new FormControl(false),
                footerLogoWidth: new FormControl('auto', [Validators.required]),
                footerLogoHeight: new FormControl('auto', [Validators.required]),
                footerCompanyLogo: new FormControl(''),
                footerSections: new FormArray<FormGroup<FooterSectionFormControls>>([]),
                welcomeTitle: new FormControl('Enter your shipment information to begin payment'),
                welcomeSubtitle: new FormControl(
                    'Discover a new level of convenience and security with our payment platform.'
                ),
            }),
        });
    }

    subscribeOrganization(): void {
        this.formAppService
            .getOrganization()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((organization: OrganizationData) => {
                const {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    userId,
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    userProfile,
                    contactPhone,
                    contactPhoneCode,
                    faqURL: _faqURL,
                    pdfURL,
                    workingScheduleForm,
                    features: { ...otherFeatures } = {},
                    primaryTones,
                    secondaryTones,
                    successTones,
                    errorTones,
                    warnTones,
                    infoTones,
                    primaryColor,
                    secondaryColor,
                    successColor,
                    errorColor,
                    warnColor,
                    infoColor,
                    paidToFacilitiesId,
                    paidToFacilityId,
                    ...organizationData
                } = organization;
                this.firebaseOrganizationData = organization;

                function safeTones(
                    tones: { lightTone?: number; primaryTone?: number; darkTone?: number } | undefined,
                    def: { lightTone: number; primaryTone: number; darkTone: number }
                ) {
                    return {
                        lightTone: tones?.lightTone ?? def.lightTone,
                        primaryTone: tones?.primaryTone ?? def.primaryTone,
                        darkTone: tones?.darkTone ?? def.darkTone,
                    };
                }
                const brandingColors = {
                    primary: {
                        color: primaryColor ?? '#016C78',
                        tones: safeTones(primaryTones, {
                            lightTone: 95,
                            primaryTone: 40,
                            darkTone: 20,
                        }),
                    },
                    secondary: {
                        color: secondaryColor ?? '#0C2245',
                        tones: safeTones(secondaryTones, {
                            lightTone: 95,
                            primaryTone: 40,
                            darkTone: 20,
                        }),
                    },
                    success: {
                        color: successColor ?? '#4caf50',
                        tones: safeTones(successTones, {
                            lightTone: 95,
                            primaryTone: 40,
                            darkTone: 20,
                        }),
                    },
                    error: {
                        color: errorColor ?? '#f44336',
                        tones: safeTones(errorTones, {
                            lightTone: 95,
                            primaryTone: 40,
                            darkTone: 20,
                        }),
                    },
                    warn: {
                        color: warnColor ?? '#fcb900',
                        tones: safeTones(warnTones, {
                            lightTone: 95,
                            primaryTone: 40,
                            darkTone: 20,
                        }),
                    },
                    info: {
                        color: infoColor ?? '#143981',
                        tones: safeTones(infoTones, {
                            lightTone: 95,
                            primaryTone: 40,
                            darkTone: 20,
                        }),
                    },
                };

                const { shipmentForm, searchForm, ...featuresData } = otherFeatures ?? {};
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { dynamicFields: shipmentDynamicFields, ...shipmentFormData } = shipmentForm ?? {};
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { dynamicFields: searchDynamicFields, ...searchFormData } = searchForm ?? {};

                const setMainLogoToHeader = organization.setMainLogoToHeader ?? true;
                const setMainLogoToLogin = organization.setMainLogoToLogin ?? true;
                const setAlternativeLogoToHeader = !setMainLogoToHeader;
                const setAlternativeLogoToLogin = !setMainLogoToLogin;

                this.form.patchValue({
                    ...organizationData,
                    paidToFacilityId: paidToFacilityId ?? null,
                    brandingColors,
                    features: {
                        ...featuresData,
                        shipmentForm: shipmentFormData,
                        searchForm: searchFormData,
                        footerCompanyLogo: otherFeatures?.footerCompanyLogo ?? undefined,
                        footerSections: undefined,
                    },
                    faqURL: _faqURL || pdfURL,
                    mainLogoLightUrl: organization.mainLogoLightUrl ?? '',
                    mainLogoDarkUrl: organization.mainLogoDarkUrl ?? '',
                    mainLogoWidth: organization.mainLogoWidth ?? 'auto',
                    mainLogoHeight: organization.mainLogoHeight ?? 'auto',
                    setMainLogoToHeader,
                    setMainLogoToLogin,
                    setAlternativeLogoToHeader,
                    setAlternativeLogoToLogin,
                });

                if (organization.domainType && organization.domainValue) {
                    this.domainHostingSection?.patchDomainValues(
                        organization.domainType as DomainType,
                        organization.domainValue
                    );
                }

                this.orgDetails?.updateWorkingScheduleFormAndPhone(workingScheduleForm, contactPhone, contactPhoneCode);
                this.paymentSettings?.updatePaymentSettingsData(
                    paidToFacilitiesId ?? [],
                    !!this.form.value.paidToMultipleFacilitiesEnable
                );
                this.phoneInputComponent?.phoneForm.patchValue({
                    phone: contactPhone,
                    code: contactPhoneCode,
                });
                this.brandingSection?.updateBrandingData(organization);
                this.legalAndTermsSectionComponent?.updateFaqURLType(_faqURL, pdfURL);
                this.featureSectionComponent?.updateOtherFeatures(otherFeatures);
                try {
                    const orgFacilities =
                        otherFeatures?.facilityInformation ?? organization.features?.facilityInformation ?? [];
                    this.featureSectionComponent?.setFacilityInformation(orgFacilities);
                } catch (e) {
                    console.warn('Error delegating facility information population', e);
                }
                try {
                    const orgFooterSections =
                        otherFeatures?.footerSections ?? organization.features?.footerSections ?? [];
                    this.featureSectionComponent?.setFooterSections(orgFooterSections);
                } catch (e) {
                    console.warn('Error delegating footer sections population', e);
                }
            });
    }

    convertToDynamicFieldOptionsForm(options: OptionsInput[] | string[] | undefined): OptionsInput[] {
        return options
            ? options.map((opt) => ({
                  value: typeof opt === 'string' ? opt : opt.value,
                  displayValue: typeof opt === 'string' ? opt : (opt.displayValue ?? opt.value ?? ''),
                  ...(typeof opt === 'object' && 'criteria' in opt && opt.criteria ? { criteria: opt.criteria } : {}),
              }))
            : [];
    }

    submit(): void {
        this.deleteEmptyBackgroundImages(this.featureSectionComponent?.backgroundImagesArray);

        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.snackBar.open('Please complete all fields required.', 'Close', {
                duration: 3000,
            });
            return;
        }

        if (this.legalAndTermsSectionComponent?.validatingFaqURL) {
            this.snackBar.open('Please wait, validating FAQ url.', 'Close', {
                duration: 3000,
            });
            return;
        }

        this.processDynamicFieldsToSave();

        // Normalize footer-related values to avoid sending `undefined` to Firestore
        const normalizedFeaturesFromFooter =
            this.featureSectionComponent?.footerSectionComponent?.normalizeFooterForSave(this.form.value.features);

        const { faqURL, contactPhone, brandingColors, domainHosting, ...formData } = this.form.value;

        const setMainLogoToHeader = this.form.value.setMainLogoToHeader ?? true;
        const setMainLogoToLogin = this.form.value.setMainLogoToLogin ?? true;
        const setAlternativeLogoToHeader = !setMainLogoToHeader;
        const setAlternativeLogoToLogin = !setMainLogoToLogin;

        const paidToMultipleFacilitiesEnable = !!this.form.value.paidToMultipleFacilitiesEnable;
        const allowShipmentForm = this.form.value.features?.typeFlow === 'shipmentForm';
        const allowSearchForm = this.form.value.features?.typeFlow === 'searchForm';
        const domainType = domainHosting?.domainType;
        const domainValue =
            domainType === 'subdomain' ? `${domainHosting?.domainValue}.cargosprint.com` : domainHosting?.domainValue;

        const organizationData: OrganizationData = <OrganizationData>{
            ...formData,
            contactPhone: contactPhone?.phone,
            contactPhoneCode: contactPhone?.code,
            faqURL: this.legalAndTermsSectionComponent?.faqURLType === FaqURLType.WEBSITE ? faqURL : null,
            pdfURL: this.legalAndTermsSectionComponent?.faqURLType === FaqURLType.PDF ? faqURL : null,
            setMainLogoToHeader,
            setMainLogoToLogin,
            setAlternativeLogoToHeader,
            setAlternativeLogoToLogin,
            primaryColor: brandingColors?.primary?.color,
            secondaryColor: brandingColors?.secondary?.color,
            successColor: brandingColors?.success?.color,
            errorColor: brandingColors?.error?.color,
            warnColor: brandingColors?.warn?.color,
            infoColor: brandingColors?.info?.color,
            primaryTones: brandingColors?.primary?.tones,
            secondaryTones: brandingColors?.secondary?.tones,
            successTones: brandingColors?.success?.tones,
            errorTones: brandingColors?.error?.tones,
            warnTones: brandingColors?.warn?.tones,
            infoTones: brandingColors?.info?.tones,
            mainLogoLightUrl: this.mainLogoLightUrlControl.value ?? undefined,
            mainLogoDarkUrl: this.mainLogoDarkUrlControl.value ?? undefined,
            mainLogoWidth: this.mainLogoWidthControl.value ?? undefined,
            mainLogoHeight: this.mainLogoHeightControl.value ?? undefined,
            paidToMultipleFacilitiesEnable,
            domainType,
            domainValue,
            features: {
                ...(normalizedFeaturesFromFooter ?? this.form.value.features),
                allowShipmentForm,
                allowSearchForm,
                shipmentForm: this.form.value.features?.shipmentForm
                    ? {
                          ...this.form.value.features.shipmentForm,
                          dynamicFields: this.dynamicFields.controls.map((dynamicField) => {
                              const fieldValue = dynamicField.value;
                              return {
                                  ...fieldValue,
                                  mask: fieldValue.validations?.some((validation) => validation.name === 'awbValidator')
                                      ? CustomMasks.iMaskAwb
                                      : fieldValue.name === 'amount'
                                        ? '{"mask":"num","blocks":{"num":{"mask":"Number","scale":2,"signed":false,"thousandsSeparator":"","padFractionalZeros":true,"normalizeZeros":true,"radix":".","mapToRadix":[","]}}}'
                                        : null,
                                  options: this.convertToDynamicFieldOptionsForm(fieldValue.options),
                              };
                          }),
                      }
                    : { dynamicFields: [] },
            },
        };
        const pastData: OrganizationData | undefined = this.firebaseOrganizationData;
        const currentData: OrganizationData = organizationData;

        if (paidToMultipleFacilitiesEnable) {
            const facilities = this.paymentSettings?.getValidPaidToFacilities() ?? [];

            if (facilities.length) {
                organizationData.paidToFacilitiesId = facilities;
            }

            delete (organizationData as Partial<OrganizationData>).paidToFacilityId;
        } else {
            const singleId = this.facilityIdControl.value;
            if (singleId !== null && singleId !== undefined) {
                organizationData.paidToFacilityId = singleId;
            }

            delete (organizationData as Partial<OrganizationData>).paidToFacilitiesId;
        }

        const dataModified = this.deletingFieldFromFormBasedOnWhichFormIsAllowed(organizationData);
        const footerSections = this.form?.controls?.features?.controls?.footerSections;
        if (footerSections) {
            for (let i = footerSections.length - 1; i >= 0; i--) {
                const section = footerSections.at(i);
                const elements = section.controls?.elements;
                if (elements) {
                    for (let j = elements.length - 1; j >= 0; j--) {
                        const val = elements.at(j).value as string | null | undefined;
                        if (!val || !val.toString().trim()) elements.removeAt(j);
                    }
                }
                const title = (section.get('title')?.value ?? '').toString().trim();
                const hasElements = elements && elements.length > 0;
                if (!title && !hasElements) {
                    footerSections.removeAt(i);
                }
            }
            dataModified.features = {
                ...dataModified.features,
                footerSections: footerSections.controls.map((s) => ({
                    title: s.get('title')?.value,
                    elements: (s.get('elements')?.value ?? []).filter((e) => !!e && e.toString().trim()),
                })),
            };
        }
        const dataModifiedPreventEmptyImages = this.preventImageArrayEmpty(dataModified);
        try {
            const facilityPayload = this.featureSectionComponent?.getFacilityInformationPayload();
            if (facilityPayload) {
                dataModifiedPreventEmptyImages.features = {
                    ...dataModifiedPreventEmptyImages.features,
                    ['facilityInformation']: facilityPayload,
                };
            } else if (
                dataModifiedPreventEmptyImages.features &&
                'facilityInformation' in dataModifiedPreventEmptyImages.features
            ) {
                delete dataModifiedPreventEmptyImages.features['facilityInformation'];
            }
        } catch (e) {
            console.warn('Failed to attach facilityInformation payload', e);
        }
        const hasRelevantChanges = this.formAppService.hasRelevantChangeInBranding(pastData, currentData);
        if (hasRelevantChanges) {
            dataModifiedPreventEmptyImages.appVersion = incrementVersion(pastData?.appVersion);
        }
        this.onSubmit.emit(dataModifiedPreventEmptyImages);
    }

    processDynamicFieldsToSave(): void {
        this.dynamicFields.controls.forEach((dynamicField, index) => {
            dynamicField.get('id')?.setValue(index + 1);
        });

        this.dynamicFields.controls.forEach((dynamicField) => {
            const validationsArray = dynamicField.get('validations') as FormArray<FormGroup<DynamicFieldValidation>>;
            validationsArray.controls.forEach((validationGroup, index) => {
                validationGroup.get('type')?.setValue('VALIDATION');
                validationGroup.get('id')?.setValue(index + 1);
                const name = validationGroup.get('name')?.value;
                if (name === 'required') {
                    validationGroup.get('message')?.setValue('This field is required');
                    validationGroup.get('value')?.setValue('true');
                } else if (name === 'pattern') {
                    validationGroup.get('message')?.setValue('Incorrect pattern');
                } else if (name === 'maxlength') {
                    validationGroup
                        .get('message')
                        ?.setValue(
                            `Maximum length exceeded (${validationGroup.get('value')?.value} characters maximum)`
                        );
                } else if (name === 'minlength') {
                    validationGroup
                        .get('message')
                        ?.setValue(
                            `Minimum length required (${validationGroup.get('value')?.value} characters minimum)`
                        );
                } else if (name === 'awbValidator') {
                    validationGroup.get('message')?.setValue('Invalid awb');
                }
            });
        });
    }

    getMaskByValidationName(fieldName: string, validations?: DynamicFieldValidation[]): string | null {
        if (fieldName === 'awb') {
            return CustomMasks.iMaskAwb;
        }
        if (fieldName === 'amount') {
            return '{"mask":"num","blocks":{"num":{"mask":"Number","scale":2,"signed":false,"thousandsSeparator":"","padFractionalZeros":true,"normalizeZeros":true,"radix":".","mapToRadix":[","]}}}';
        }
        if (
            validations &&
            validations.some(
                (v) => v.name && (typeof v.name === 'string' ? v.name === 'customMask' : v.name.value === 'customMask')
            )
        ) {
            return 'custom-mask-value';
        }
        return null;
    }

    initEdition(): void {
        this.form.controls['organizationName'].disable();
    }

    assignNamesExtraFields(): void {
        const extraFields: Array<FormGroup<DynamicFieldForm>> = this.getExtraFieldsControls();
        extraFields.forEach((ctrl: FormGroup<DynamicFieldForm>, index: number) => {
            ctrl.get('name')?.setValue(`${DynamicFieldBaseName}${index + 1}`);
        });
    }

    getExtraFieldsControls(): Array<FormGroup<DynamicFieldForm>> {
        return this.dynamicFields.controls.filter((ctrl: FormGroup<DynamicFieldForm>) => {
            const name: string = ctrl.get('name')?.value as string;
            return name && this.extraFieldNameRegex.test(name);
        });
    }

    deletingFieldFromFormBasedOnWhichFormIsAllowed(organizationData: OrganizationData): OrganizationData {
        if (organizationData.features?.allowShipmentForm) {
            delete organizationData.features?.searchForm;
        }
        if (organizationData.features?.allowSearchForm) {
            delete organizationData.features?.shipmentForm;
        }
        return organizationData;
    }

    deleteEmptyBackgroundImages(
        formArray: FormArray<FormGroup<{ name: FormControl<string | null> }>> | undefined
    ): void {
        if (!formArray) return;
        for (let i = formArray.length - 1; i >= 0; i--) {
            const imageGroup = formArray.at(i) as FormGroup<{ name: FormControl<string | null> }>;
            const imageName = imageGroup.get('name')?.value;
            if (!imageName) {
                formArray.removeAt(i);
            }
        }
    }

    preventImageArrayEmpty(organizationData: OrganizationData): OrganizationData {
        const hasName = (img: { name?: string | null } | null | undefined) => !!img?.name && img.name.trim().length > 0;

        if (organizationData.features?.searchForm?.backgroundImages) {
            organizationData.features.searchForm.backgroundImages =
                organizationData.features.searchForm.backgroundImages.filter(hasName);
        }

        if (organizationData.features?.shipmentForm?.backgroundImages) {
            organizationData.features.shipmentForm.backgroundImages =
                organizationData.features.shipmentForm.backgroundImages.filter(hasName);
        }

        return organizationData;
    }
}
